<template>
  <b-container v-if="forbidden">
    <ForbiddenBanner/>
  </b-container>
  <b-container v-else-if="program">
    <b-row class="text-left">
      <b-col>
        <h2>
          {{ program.name }}&#8205;
          <b-badge class="ml-2 align-middle h6" v-if="! program.active">Inactive</b-badge>
        </h2>
        <h5 class="text-secondary">
          <font-awesome-icon icon="sitemap" size="1x"/>
          {{ globalData.currentOrganization.name }}
        </h5>
      </b-col>
    </b-row>

    <b-row class="border shadow-lg user-select-none bg-light text-left mt-2 pt-2 pb-2" align-v="end">
      <b-col md="6" lg="5">
        <b-form-group label="Name" label-size="sm" class="mb-0">
          <b-form-input v-model="name" @change="onUpdate({name: name})"/>
        </b-form-group>
      </b-col>
      <b-col md="6" lg="4">
        <b-form-group label="Starts (first day of week 1)" label-size="sm" class="mb-0">
          <b-form-datepicker
              v-model="startDate"
              :start-weekday="0"
              :date-disabled-fn="isNotFirstDayOfWeek"
              :date-format-options="{year: 'numeric', month: 'short', day: '2-digit', weekday: 'short'}"
              @input="onStartDateChange"
          />
        </b-form-group>
      </b-col>
      <b-col lg="3" class="text-right mt-2 mt-lg-0">
        <b-button variant="success" @click="$bvModal.show('import-workouts-modal')">Import Workouts</b-button>
      </b-col>
    </b-row>
    <br/>

    <BulkCreateWorkoutsModal
        id="import-workouts-modal"
        :title="`Import Workouts into ${program.name}`"
        ok-title="Import"
        :program="program"
        @created="refresh"
    />

    <!-- One table for all the weeks, so their columns line up. Week null is the workouts in the program without a week. -->
    <div class="text-left mb-3">
      <table class="table table-sm table-hover mb-0 program-table">
        <tbody v-for="(week, index) in sections" :key="sectionKey(week)">
          <tr v-if="index > 0" class="week-gap" :class="{'group-gap': startsGroup(index)}">
            <td colspan="5"></td>
          </tr>
          <!-- Weeks that already ended, grouped in one collapsible row.
               Header cells follow the workout columns (name, labels, time, distance, actions), so they line up. -->
          <tr v-if="week === PAST" class="week-header past-weeks" @click="showPastWeeks = !showPastWeeks">
            <td>
              <font-awesome-icon :icon="showPastWeeks ? 'chevron-down' : 'chevron-right'" class="text-secondary mr-2" fixed-width/>
              <b>Past weeks</b>
              <span class="text-secondary ml-2">{{ groupTitle(pastWeeks) }}</span>
            </td>
            <td class="d-none d-sm-table-cell fit text-nowrap text-secondary small">{{ groupWorkoutCount(pastWeeks) }} workouts</td>
            <td class="d-none d-md-table-cell fit"></td>
            <td class="d-none d-md-table-cell fit"></td>
            <td class="fit"></td>
          </tr>
          <!-- Weeks after the current one, grouped in one collapsible row like the past weeks -->
          <tr v-else-if="week === UPCOMING" class="week-header upcoming-weeks" @click="showUpcomingWeeks = !showUpcomingWeeks">
            <td>
              <font-awesome-icon :icon="showUpcomingWeeks ? 'chevron-down' : 'chevron-right'" class="text-secondary mr-2" fixed-width/>
              <b>Upcoming weeks</b>
              <span class="text-secondary ml-2">{{ groupTitle(upcomingWeeks) }}</span>
            </td>
            <td class="d-none d-sm-table-cell fit text-nowrap text-secondary small">{{ groupWorkoutCount(upcomingWeeks) }} workouts</td>
            <td class="d-none d-md-table-cell fit"></td>
            <td class="d-none d-md-table-cell fit"></td>
            <td class="fit"></td>
          </tr>
          <template v-else>
          <tr class="week-header" :class="{'no-week': !week}">
            <td>
              <template v-if="week">
                <b>Week {{ week }}</b>
                <span class="text-secondary ml-2">{{ weekRange(week) }}</span>
              </template>
              <template v-else>
                <b>No week</b>
                <span class="text-secondary ml-2">In the program, not assigned to a week</span>
              </template>
            </td>
            <td class="d-none d-sm-table-cell fit text-nowrap text-secondary small">{{ workoutsOfWeek(week).length }} workouts</td>
            <td class="d-none d-md-table-cell fit text-nowrap text-secondary">
              <Duration v-if="weekDuration(week) > 0" :duration="weekDuration(week)" textClass="small"/>
            </td>
            <td class="d-none d-md-table-cell fit text-nowrap text-right text-secondary small">
              <span v-if="weekDistance(week) > 0">{{ formatKm(weekDistance(week)) }} km</span>
            </td>
            <td class="fit text-right">
              <!-- Only the current and later weeks can be published -->
              <b-button
                  v-if="week && !isPastWeek(week)"
                  size="sm"
                  variant="outline-success"
                  class="py-0 text-nowrap"
                  :disabled="activeWorkoutsOfWeek(week).length === 0 || publishingWeek === week"
                  :title="`Publish the ${activeWorkoutsOfWeek(week).length} active workouts of week ${week}`"
                  @click="onPublishWeek(week)"
              >
                Publish
                <font-awesome-icon icon="share"/>
              </b-button>
            </td>
          </tr>
          <tr
              v-for="item in workoutsOfWeek(week)"
              :key="item.key"
              @mouseenter="hoveredKey = item.key"
              @mouseleave="hoveredKey = null"
          >
            <td>
              <router-link :to="{ name: 'workout', params: { uuid: item.workoutUuid }}">{{ item.name }}</router-link>
              <b-badge class="ml-2" v-if="! item.active">Inactive</b-badge>
            </td>
            <td class="d-none d-sm-table-cell fit">
              <LabelSelector :globalData="globalData" :readOnly="true" :selectedLabelUuids="item.labelUuids"/>
            </td>
            <td class="d-none d-md-table-cell fit text-nowrap">
              <Duration v-if="item.duration > 0" :duration="item.duration" textClass="small"/>
            </td>
            <td class="d-none d-md-table-cell fit text-nowrap text-right">
              <span v-if="item.distance > 0" class="small">{{ formatKm(item.distance) }} km</span>
            </td>
            <td class="fit">
              <!-- Always rendered (only hidden), so hovering doesn't change the column widths -->
              <font-awesome-icon
                  class="row-button mx-2 text-secondary"
                  :class="{invisible: item.key !== hoveredKey}"
                  icon="trash"
                  :title="week ? `Remove from week ${week}` : 'Remove from program'"
                  size="1x"
                  @click.stop="onRemoveWorkout(week, item.workoutUuid)"
              />
            </td>
          </tr>
          <tr class="add-row" v-if="week">
            <td colspan="5">
              <WorkoutPicker
                  :options="workoutOptions(week)"
                  placeholder="Add a workout to this week (type to search)"
                  @select="workoutUuid => onAddWorkout(week, workoutUuid)"
              />
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div class="text-left mb-4">
      <b-button variant="outline-success" size="sm" @click="onAddWeek">Add Week {{ weeks.length + 1 }}</b-button>
      <span class="text-secondary small ml-2" v-if="extraWeeks > 0">Weeks without workouts are not saved</span>
    </div>
  </b-container>
</template>

<script>
import BulkCreateWorkoutsModal from "@/components/Workouts/BulkCreateWorkoutsModal";
import Duration from "@/components/Duration.vue";
import ForbiddenBanner from "@/components/ForbiddenBanner";
import ForbiddenContentMixin from "@/components/ForbiddenContentMixin";
import LabelSelector from "@/components/Labels/LabelSelector";
import ProgramMixin from "@/components/Programs/ProgramMixin";
import WorkoutPicker from "@/components/Programs/WorkoutPicker";

export default {
  name: 'Program',
  mixins: [ForbiddenContentMixin, ProgramMixin],
  components: {
    BulkCreateWorkoutsModal,
    Duration,
    ForbiddenBanner,
    LabelSelector,
    WorkoutPicker,
  },
  props: {
    uuid: String,
  },
  data() {
    return {
      program: null,
      name: '',
      startDate: null,
      workouts: [],
      extraWeeks: 0,
      showPastWeeks: false,
      showUpcomingWeeks: false,
      // Stand in the sections for the collapsible "Past weeks" and "Upcoming weeks" rows
      PAST: 'past',
      UPCOMING: 'upcoming',
      publishingWeek: null,
      hoveredKey: null,
      rangeFormatter: new Intl.DateTimeFormat('default', {
        month: 'short',
        day: '2-digit',
      }),
    }
  },
  computed: {
    workoutsByUuid() {
      return Object.fromEntries(this.workouts.map(workout => [workout.uuid, workout]));
    },
    weeks() {
      const count = Math.max(this.weekCount(this.program), 1) + this.extraWeeks;
      return Array.from({length: count}, (_, i) => i + 1);
    },
    currentWeek() {
      // The week today is in, or week 1 when the program hasn't started yet (the next one to run)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.round((today - this.parseDate(this.program.startDate)) / (24 * 60 * 60 * 1000));
      return days < 0 ? 1 : Math.floor(days / 7) + 1;
    },
    pastWeeks() {
      return this.weeks.filter(week => this.isPastWeek(week));
    },
    upcomingWeeks() {
      return this.weeks.filter(week => week > this.currentWeek);
    },
    sections() {
      const sections = [];
      // The "no week" section is only shown when it has workouts
      if (this.program.workouts.some(item => item.week == null)) {
        sections.push(null);
      }
      // Past weeks are collapsed into one row unless expanded
      if (this.pastWeeks.length > 0) {
        sections.push(this.PAST);
        if (this.showPastWeeks) {
          sections.push(...this.pastWeeks);
        }
      }
      sections.push(...this.weeks.filter(week => week === this.currentWeek && !this.isPastWeek(week)));
      // Upcoming weeks are collapsed into one row too
      if (this.upcomingWeeks.length > 0) {
        sections.push(this.UPCOMING);
        if (this.showUpcomingWeeks) {
          sections.push(...this.upcomingWeeks);
        }
      }
      return sections;
    },
  },
  created() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$http.get(`/api/programs/${this.uuid}`)
          .then(response => {
            this.forbidden = false;
            this.setProgram(response.data);
          })
          .catch(e => {
            this.HandleResponse(e)
          })
      // Include inactive workouts, the program may still use them
      this.$http.get('/api/workouts', {params: {'showInactive': true}})
          .then(response => {
            this.workouts = response.data
          })
          .catch(e => {
            console.warn(e)
          })
    },
    setProgram(program) {
      // Weeks that got workouts are no longer extra
      const shownWeeks = this.program ? this.weeks.length : 0;
      this.program = program;
      this.extraWeeks = Math.max(0, shownWeeks - Math.max(this.weekCount(program), 1));
      this.name = program.name;
      this.startDate = program.startDate;
    },
    workoutOptions(week) {
      // Active workouts that aren't in this week yet
      const inWeek = new Set(this.program.workouts.filter(item => item.week === week).map(item => item.workoutUuid));
      return this.workouts
          .filter(workout => workout.active && !inWeek.has(workout.uuid))
          .map(workout => ({value: workout.uuid, text: workout.name}))
          .sort((a, b) => a.text.localeCompare(b.text));
    },
    sectionGroup(week) {
      // Sections are grouped: past weeks, workouts without a week, the current week, and upcoming weeks
      if (week == null) {
        return 'no-week';
      }
      if (week === this.UPCOMING || week > this.currentWeek) {
        return 'upcoming';
      }
      return week === this.PAST || this.isPastWeek(week) ? 'past' : 'current';
    },
    groupTitle(weeks) {
      const first = weeks[0];
      const last = weeks[weeks.length - 1];
      const title = first === last ? `Week ${first}` : `Weeks ${first}–${last}`;
      return `${title} · ${this.weekRange(first, last)}`;
    },
    groupWorkoutCount(weeks) {
      return weeks.reduce((total, week) => total + this.workoutsOfWeek(week).length, 0);
    },
    onAddWeek() {
      // The new week is an upcoming one, show it
      this.extraWeeks++;
      this.showUpcomingWeeks = true;
    },
    startsGroup(index) {
      // Only called for sections after the first one (the first has no gap above it)
      return this.sectionGroup(this.sections[index]) !== this.sectionGroup(this.sections[index - 1]);
    },
    sectionKey(week) {
      return week || 'none';
    },
    isNotFirstDayOfWeek(ymd, date) {
      return date.getDay() !== 0;
    },
    weekStartDate(week) {
      const date = this.parseDate(this.program.startDate);
      date.setDate(date.getDate() + (week - 1) * 7);
      return date;
    },
    weekEndDate(week) {
      const date = this.weekStartDate(week);
      date.setDate(date.getDate() + 6);
      return date;
    },
    isPastWeek(week) {
      // The week ended before today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.weekEndDate(week) < today;
    },
    weekRange(firstWeek, lastWeek = firstWeek) {
      return `${this.rangeFormatter.format(this.weekStartDate(firstWeek))} – ${this.dateFormatter.format(this.weekEndDate(lastWeek))}`;
    },
    workoutsOfWeek(week) {
      return this.program.workouts
          .filter(item => (item.week || null) === week)
          .map(item => {
            const workout = this.workoutsByUuid[item.workoutUuid];
            const meta = workout && workout.meta ? workout.meta : {};
            return {
              key: `${week}/${item.workoutUuid}`,
              workoutUuid: item.workoutUuid,
              name: workout ? workout.name : '…',
              active: workout ? workout.active : true,
              labelUuids: meta.labelUuids || [],
              duration: meta.duration || 0,
              distance: meta.distance || 0,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
    },
    weekDuration(week) {
      return this.workoutsOfWeek(week).reduce((total, item) => total + item.duration, 0);
    },
    weekDistance(week) {
      return this.workoutsOfWeek(week).reduce((total, item) => total + item.distance, 0);
    },
    formatKm(meters) {
      // Always one decimal (10.0, 21.1), so the distances line up
      return (meters / 1000).toFixed(1);
    },
    onUpdate(data) {
      this.$http.post(`/api/programs/${this.uuid}`, data)
          .then(response => {
            this.setProgram(response.data);
          })
          .catch(e => {
            this.showError(e);
            this.setProgram(this.program);
          })
    },
    onStartDateChange(value) {
      if (value && value !== this.program.startDate) {
        this.onUpdate({startDate: value});
      }
    },
    workoutUrl(week, workoutUuid) {
      // Without a week, the workout is added to (or removed from) the program itself
      return week
          ? `/api/programs/${this.uuid}/weeks/${week}/workouts/${workoutUuid}`
          : `/api/programs/${this.uuid}/workouts/${workoutUuid}`;
    },
    onAddWorkout(week, workoutUuid) {
      this.$http.put(this.workoutUrl(week, workoutUuid))
          .then(response => {
            this.setProgram(response.data);
          })
          .catch(e => {
            this.showError(e);
          })
    },
    onRemoveWorkout(week, workoutUuid) {
      this.$http.delete(this.workoutUrl(week, workoutUuid))
          .then(response => {
            this.setProgram(response.data);
          })
          .catch(e => {
            this.showError(e);
          })
    },
    activeWorkoutsOfWeek(week) {
      // Inactive workouts aren't published
      return this.workoutsOfWeek(week).filter(item => item.active);
    },
    onPublishWeek(week) {
      const workouts = this.activeWorkoutsOfWeek(week);
      this.publishingWeek = week;
      this.$http.post('/api/workouts/publish', {workout_uuids: workouts.map(item => item.workoutUuid)})
          .then(() => {
            this.$bvToast.toast(`Published to ${this.globalData.currentOrganization.name} Runners`, {
              title: `Week ${week}: ${workouts.length} workouts`,
              toaster: 'b-toaster-bottom-center',
              variant: 'success',
              solid: true,
              autoHideDelay: 2000,
              noCloseButton: true,
              appendToast: true,
            });
          })
          .catch(e => {
            this.showError(e, `Week ${week} not published`, 'Only admins and owners can publish workouts');
          })
          .finally(() => {
            this.publishingWeek = null;
          })
    },
    showError(e, title = 'Program not updated', forbiddenMessage = 'You are not allowed to do this') {
      let message = e.response && typeof e.response.data === 'string' && e.response.data ? e.response.data : 'Something went wrong';
      if (e.response && e.response.status === 403) {
        message = forbiddenMessage;
      }
      this.$bvToast.toast(message, {
        title: title,
        toaster: 'b-toaster-bottom-center',
        variant: 'danger',
        solid: true,
        autoHideDelay: 4000,
        appendToast: true,
      });
    },
  }
}
</script>

<style scoped>
.row-button {
  cursor: pointer;
}

/* Columns other than the name take only the width they need, the same for all the weeks */
.program-table td.fit {
  width: 1%;
  padding-left: 1rem;
}

/*
 * Each week section (the "no week" section included) is outlined separately, with an 8px gap between them.
 * Borders are drawn per cell (separate, not collapsed), so the gap rows can stay borderless.
 */
.program-table {
  border-collapse: separate;
  border-spacing: 0;
}

.program-table tbody + tbody {
  border-top: none;
}

.program-table td:first-child {
  border-left: 1px solid #dee2e6;
}

.program-table td:last-child {
  border-right: 1px solid #dee2e6;
}

.program-table tbody tr:last-child td {
  border-bottom: 1px solid #dee2e6;
}

.program-table tr.week-gap td {
  height: 8px;
  padding: 0;
  border: none;
}

/* 8px more between the groups (past weeks, no week, current and later weeks) than between weeks of the same group */
.program-table tr.week-gap.group-gap td {
  height: 16px;
}

.program-table tr.week-header td {
  background-color: rgba(0, 0, 0, 0.03);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.program-table tr.week-header.no-week td {
  background-color: #fff3cd;
}

/* Only the workout rows highlight on hover */
.program-table tr.past-weeks {
  cursor: pointer;
}

.table-hover tbody tr.week-header:hover,
.table-hover tbody tr.week-gap:hover,
.table-hover tbody tr.add-row:hover {
  background-color: transparent;
}
</style>
